import { fontAttrs } from "@/components/tools/printables/page-kit";
import {
  type Box,
  type FaceLayout,
  type FaceOptions,
  layoutFace,
  nameFont,
} from "@/lib/tool-kit/printables/name-tags";
import { r2 } from "@/lib/tool-kit/printables/page";
import type { TextMeasure } from "@/lib/tool-kit/printables/text";

const INK = "#1a1a1a";
const CUT = "#9a9a9a";

/** One face: frame or band, header, the name (or a write-in line) and the detail line. */
export function TagFace({
  face,
  options,
  accent,
}: {
  face: FaceLayout;
  options: FaceOptions;
  accent: string;
}) {
  const font = nameFont(options.font);
  return (
    <g>
      {face.band ? (
        <rect
          fill={accent}
          height={r2(face.band.height)}
          width={r2(face.band.width)}
          x={r2(face.band.x)}
          y={r2(face.band.y)}
        />
      ) : null}
      {face.frame ? (
        <rect
          fill="none"
          height={r2(face.frame.height)}
          rx={2.5}
          stroke={accent}
          strokeWidth={0.8}
          width={r2(face.frame.width)}
          x={r2(face.frame.x)}
          y={r2(face.frame.y)}
        />
      ) : null}
      {face.header ? (
        <text
          fill="#ffffff"
          fontSize={face.header.size}
          textAnchor="middle"
          x={face.header.x}
          y={face.header.y}
          {...fontAttrs({ kind: "sans", bold: true })}
        >
          {face.header.text}
        </text>
      ) : null}
      {face.name ? (
        <text
          fill={INK}
          fontSize={face.name.size}
          textAnchor="middle"
          x={face.name.x}
          y={face.name.y}
          {...fontAttrs(font)}
        >
          {face.name.text}
        </text>
      ) : null}
      {face.rule ? (
        <line
          stroke={INK}
          strokeWidth={0.35}
          x1={face.rule.x1}
          x2={face.rule.x2}
          y1={face.rule.y}
          y2={face.rule.y}
        />
      ) : null}
      {face.detail ? (
        <text
          fill="#444444"
          fontSize={face.detail.size}
          textAnchor="middle"
          x={face.detail.x}
          y={face.detail.y}
          {...fontAttrs({ kind: "sans" })}
        >
          {face.detail.text}
        </text>
      ) : null}
    </g>
  );
}

function Outline({ box }: { box: Box }) {
  return (
    <rect
      fill="none"
      height={r2(box.height)}
      stroke={CUT}
      strokeDasharray="2 1.5"
      strokeWidth={0.25}
      width={r2(box.width)}
      x={r2(box.x)}
      y={r2(box.y)}
    />
  );
}

export interface TagContent {
  name: string;
  detail: string;
}

/** A page of badges: each tag in its cell with a dashed cutting outline. */
export function BadgeSheet({
  cells,
  tags,
  options,
  accent,
  measure,
}: {
  cells: readonly Box[];
  tags: readonly TagContent[];
  options: Omit<FaceOptions, "name" | "detail">;
  accent: string;
  measure: TextMeasure;
}) {
  return (
    <g>
      {cells.map((cell, index) => {
        const tag = tags[index];
        if (!tag) {
          return null;
        }
        const faceOptions = { ...options, ...tag };
        return (
          <g key={`${cell.x}-${cell.y}`}>
            <TagFace
              accent={accent}
              face={layoutFace(cell, measure, faceOptions)}
              options={faceOptions}
            />
            <Outline box={cell} />
          </g>
        );
      })}
    </g>
  );
}

/** A page of folding desk plates: top panel upside down, a fold line, bottom panel upright. */
export function PlateSheet({
  plates,
  panelHeight,
  tags,
  options,
  accent,
  measure,
}: {
  plates: readonly Box[];
  panelHeight: number;
  tags: readonly TagContent[];
  options: Omit<FaceOptions, "name" | "detail">;
  accent: string;
  measure: TextMeasure;
}) {
  return (
    <g>
      {plates.map((plate, index) => {
        const tag = tags[index];
        if (!tag) {
          return null;
        }
        const faceOptions = { ...options, ...tag };
        const top: Box = { ...plate, height: panelHeight };
        const bottom: Box = {
          ...plate,
          y: plate.y + panelHeight,
          height: panelHeight,
        };
        const centreX = plate.x + plate.width / 2;
        const centreY = plate.y + panelHeight / 2;
        const foldY = plate.y + panelHeight;
        return (
          <g key={plate.y}>
            <g transform={`rotate(180 ${r2(centreX)} ${r2(centreY)})`}>
              <TagFace
                accent={accent}
                face={layoutFace(top, measure, faceOptions)}
                options={faceOptions}
              />
            </g>
            <TagFace
              accent={accent}
              face={layoutFace(bottom, measure, faceOptions)}
              options={faceOptions}
            />
            <Outline box={plate} />
            <line
              stroke={CUT}
              strokeDasharray="4 2"
              strokeWidth={0.35}
              x1={r2(plate.x)}
              x2={r2(plate.x + plate.width)}
              y1={r2(foldY)}
              y2={r2(foldY)}
            />
          </g>
        );
      })}
    </g>
  );
}
